import moment from "moment";
import * as React from "react";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { useParams } from "react-router";
import { bindActionCreators } from "redux";
import ToggleButton from "src/components/common/Buttons/ToggleButton";
import Tree, { ITree } from "src/components/common/Tree/Tree";
import { TrialPersonnelsBreadcrumbs } from "src/helpers/breadcrumbHelper";
import useNavigate from "src/hooks/useNavigate";
import * as NavigationActions from "../../../actions/navigation";
import * as TrialActions from "../../../actions/trial";
import * as TrialGroupActions from "../../../actions/trialGroup";
import * as TrialInstitutionActions from "../../../actions/trialInstitution";
import * as TrialPersonnelActions from "../../../actions/trialPersonnel";
import { InlineLoading } from "../../../components/common/index";
import { Link } from "../../../components/routing/index";
import { FontAwesomeIcon, FontAwesomeIcons } from "../../../constants/fontAwesomeIcons";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import * as AuthenticationHelper from "../../../helpers/authentication";
import { TrialSubMenu } from "../../../helpers/subMenuHelper";
import { ITmdState } from "../../../reducers/index";
import { TrialLayout } from "../../index";

import "./TrialPersonnelsPage.scss";

interface TrialPersonnelPageProps {
    trial: Dtos.Trial;
    loadingTrial: boolean;
    loadTrialSuccess: boolean;
    loadTrialFailure: boolean;
    loadTrialErrorCode: string;
    trialPersonnel: Dtos.TrialPersonnel[];
    loadingTrialPersonnel: boolean;
    loadTrialPersonnelSuccess: boolean;
    loadTrialPersonnelFailure: boolean;
    loadTrialPersonnelErrorCode: string;
    trialInstitutions: Dtos.TrialInstitution[];
    loadingTrialInstitutions: boolean;
    loadTrialInstitutionsSuccess: boolean;
    loadTrialInstitutionsFailure: boolean;
    loadTrialInstitutionsErrorCode: string;
    trialGroups: Dtos.TrialGroup[];
    loadingTrialGroups: boolean;
    loadTrialGroupsSuccess: boolean;
    loadTrialGroupsFailure: boolean;
    loadTrialGroupsErrorCode: string;
    permissions?: Dtos.PersonnelTmdPermissions[];
    clearTrial: TrialActions.ITrialClearActionCreator;
    loadTrial: TrialActions.ITrialLoadByIdActionCreator;
    clearTrialPersonnel: TrialPersonnelActions.ITrialPersonnelClearActionCreator;
    loadTrialPersonnel: TrialPersonnelActions.ITrialPersonnelsLoadByTrialIdActionCreator;
    clearTrialInstitutions: TrialInstitutionActions.ITrialInstitutionClearActionCreator;
    loadTrialInstitutions: TrialInstitutionActions.ITrialInstitutionsLoadByTrialIdActionCreator;
    clearTrialGroups: TrialGroupActions.ITrialGroupClearActionCreator;
    loadTrialGroups: TrialGroupActions.ITrialGroupsLoadByTrialIdActionCreator;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
}

const TrialPersonnelsPage: FC<TrialPersonnelPageProps> = ({
    trial,
    loadingTrial,
    loadTrialSuccess,
    loadTrialFailure,
    loadTrialErrorCode,
    trialPersonnel,
    loadingTrialPersonnel,
    loadTrialPersonnelSuccess,
    loadTrialPersonnelFailure,
    loadTrialPersonnelErrorCode,
    trialInstitutions,
    loadingTrialInstitutions,
    loadTrialInstitutionsSuccess,
    loadTrialInstitutionsFailure,
    loadTrialInstitutionsErrorCode,
    trialGroups,
    loadingTrialGroups,
    loadTrialGroupsSuccess,
    loadTrialGroupsFailure,
    loadTrialGroupsErrorCode,
    permissions,
    clearTrial,
    loadTrial,
    clearTrialPersonnel,
    loadTrialPersonnel,
    clearTrialInstitutions,
    loadTrialInstitutions,
    clearTrialGroups,
    loadTrialGroups,
    setBreadcrumbs,
    setDrawerItems
}) => {
    //
    // -- Params --
    const { trialId } = useParams<{ trialId: string }>();
    const trialIdNumber = parseInt(trialId, 10);

    //
    // -- States --
    const [showCeased, setShowCeased] = useState<boolean>(false);
    const [treeData, setTreeData] = useState<ITree | null>(null);
    const [initalizedTreeData, setInitalizedTreeData] = useState<boolean>(false);
    const [toggleState, setToggleState] = useState<string[]>([]);
    const [creatingTree, setCreatingTree] = useState<boolean>(false);

    //
    // -- Hooks --
    const navigate = useNavigate();

    //
    // -- Variables --
    const showTrialPersonnels =
        !loadingTrial && 
        loadTrialSuccess && 
        !loadingTrialPersonnel && 
        loadTrialPersonnelSuccess &&
        !loadingTrialInstitutions &&
        loadTrialInstitutionsSuccess &&
        !loadingTrialGroups &&
        loadTrialGroupsSuccess &&
        !creatingTree;


    // Load Trial & Personnel
    useEffect(() => {
        if (trialIdNumber && !trial) {
            setDrawerItems(TrialSubMenu(trialIdNumber, permissions));
            setBreadcrumbs(TrialPersonnelsBreadcrumbs(trialIdNumber));

            setTreeData(null);

            loadTrial(trialIdNumber);
            loadTrialPersonnel(trialIdNumber, undefined);
            loadTrialInstitutions(trialIdNumber);
            loadTrialGroups(trialIdNumber)
        }
    }, [
        trialIdNumber,
        trial,
        permissions,
        setDrawerItems,
        setBreadcrumbs,
        loadTrial,
        TrialSubMenu,
        TrialPersonnelsBreadcrumbs,
        setTreeData,
        loadTrialPersonnel,
        loadTrialInstitutions,
        loadTrialGroups
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadTrialFailure) {
            navigate(`/error/${loadTrialErrorCode}`);
        }
        if (loadTrialPersonnelFailure) {
            navigate(`/error/${loadTrialPersonnelErrorCode}`);
        }
        if (loadTrialInstitutionsFailure) {
            navigate(`/error/${loadTrialInstitutionsErrorCode}`);
        }
        if (loadTrialGroupsFailure) {
            navigate(`/error/${loadTrialGroupsErrorCode}`);
        }
    }, [
        loadTrialFailure, 
        loadTrialErrorCode, 
        loadTrialPersonnelFailure, 
        loadTrialPersonnelErrorCode, 
        loadTrialInstitutionsFailure,
        loadTrialInstitutionsErrorCode,
        loadTrialGroupsFailure,
        loadTrialGroupsErrorCode,
        navigate
    ]);

    // Update breadcrumbs
    useEffect(() => {
        if (trialIdNumber && trial?.displayName) {
            setDrawerItems(TrialSubMenu(trialIdNumber, permissions));
            setBreadcrumbs(TrialPersonnelsBreadcrumbs(trialIdNumber, trial.displayName));
        }
    }, [
        trial,
        trialIdNumber,
        permissions,
        setDrawerItems,
        setBreadcrumbs,
        TrialSubMenu,
        TrialPersonnelsBreadcrumbs,
    ]);

    // Cleanup effect to clear the trial on component unmount
    useEffect(() => {
        return () => {
            setTreeData(null);
            clearTrial();
            clearTrialPersonnel();
            clearTrialInstitutions();
            clearTrialGroups();
        };
    }, [
        clearTrial, 
        clearTrialPersonnel,
        clearTrialInstitutions, 
        clearTrialGroups,
        setTreeData
    ]);

    // Create tree
    useEffect(() => {
        if (trialPersonnel && !initalizedTreeData) {
            setCreatingTree(true);

            // If no trial personnel return
            if (trialPersonnel?.length == 0) {
                setCreatingTree(false);
                return;
            }

            const createPersonnelNodes = (personnel: Dtos.TrialPersonnel[], showCeased: boolean, nodeIdPrefix: string) => {
                let children =
                    personnel
                        ?.filter((x) => showCeased ? true : !x.dateCeased)
                        ?.sort((a, b) => a?.id - b?.id)
                        ?.map((x) => ({
                            id: `${nodeIdPrefix}-personnel-${x.id}`,
                            data: (
                                <div className={`form-row ${showCeased ? x?.dateCeased ? "ceased" : "" : ""}`}>
                                    <div className="col-1">
                                        <FontAwesomeIcon icon={FontAwesomeIcons.Duotone.USER} />
                                    </div>
                                    <div className="col">
                                        {x?.personnel?.displayWithSalutation}
                                    </div>
                                    <div className="col">
                                        {
                                            x?.dateStarted
                                                ? moment(x?.dateStarted, "YYYY-MM-DD HH:mm:ss.SSSSSSS Z").format("DD/MM/YYYY")
                                                : ""
                                        }
                                    </div>
                                    <div className="col">
                                        {
                                            x?.dateCeased
                                                ? moment(x?.dateCeased, "YYYY-MM-DD HH:mm:ss.SSSSSSS Z").format("DD/MM/YYYY")
                                                : ""
                                        }
                                    </div>
                                </div>
                            ),
                            icon: null,
                            collapsible: false,
                        })) ?? [];

                // Add table header
                if (children?.length > 0) {
                    children.unshift({
                        id: `tableheader`,
                        data: (
                            <div className="form-row">
                                <div className="col-1"></div>
                                <div className="col">
                                    <strong>Name</strong>
                                </div>
                                <div className="col">
                                    <strong>Date Started</strong>
                                </div>
                                <div className="col">
                                    <strong>Date Ceased</strong>
                                </div>
                            </div>
                        ),
                        icon: null,
                        collapsible: false,
                    });
                }

                return children;
            };

            const createRoleNodes = (personnel: Dtos.TrialPersonnel[], showCeased: boolean, nodeIdPrefix: string) => {
                let uniqueRoles: Dtos.Role[] = Array.from(
                    new Map(
                        personnel
                            ?.filter((x) => showCeased ? true : !x.dateCeased)
                            ?.map((x) => [x.role.id, x.role])
                    ).values()
                );

                let children =
                    uniqueRoles
                        ?.sort((a, b) => a?.value?.localeCompare(b?.value))
                        ?.map((x) => ({
                            id: `${nodeIdPrefix}-role-${x.id}`,
                            data: (
                                <div className="d-flex align-items-center">
                                    <FontAwesomeIcon icon={FontAwesomeIcons.Duotone.ROLE} />
                                    <span
                                        className="pl-4 mb-0"
                                        style={{ width: "275px" }}
                                    >
                                        {x?.value}
                                    </span>
                                    <div
                                        className="d-flex align-items-center justify-content-center"
                                        style={{ width: "50px" }}
                                    >
                                        <span className="badge badge-pill badge-secondary ml-4 mb-0">
                                            {
                                                personnel?.filter((y) =>
                                                    showCeased
                                                        ? y.roleId == x.id
                                                        : y.roleId == x.id &&
                                                        !y.dateCeased
                                                )?.length
                                            }
                                        </span>
                                    </div>
                                </div>
                            ),
                            icon: null,
                            collapsible: true,
                            subTree: {
                                children: [
                                    ...createPersonnelNodes(
                                        personnel?.filter((z) => z.roleId == x.id), 
                                        showCeased, 
                                        `${nodeIdPrefix}-role-${x.id}`
                                    )
                                ],
                            },
                        })) ?? [];
                return children;
            }

            const createTrialNodes = (trials: Dtos.Trial[], personnel: Dtos.TrialPersonnel[], showCeased: boolean) => {
                let trialNodes =
                    trials
                        ?.sort((a, b) => a?.studyAcronym.localeCompare(b?.studyAcronym))
                        ?.map((x) => ({
                            id: `trial-${x.id}`,
                            data: (
                                <div className="d-flex align-items-center">
                                    <div
                                        className="d-flex align-items-center justify-content-center"
                                        style={{ width: "25px" }}
                                    >
                                        <FontAwesomeIcon icon={FontAwesomeIcons.Duotone.TRIAL} />
                                    </div>
                                    <span
                                        className="pl-4 mb-0"
                                        style={{ width: "450px" }}
                                    >
                                        {x?.studyAcronym}
                                    </span>
                                    <div
                                        className="d-flex align-items-center justify-content-center"
                                        style={{ width: "50px" }}
                                    >
                                        <span className="badge badge-pill badge-secondary ml-4 mb-0">
                                            {
                                                personnel?.filter((y) =>
                                                    showCeased
                                                        ? y.trialId == x.id && y.institutionId == null && y.groupId == null
                                                        : y.trialId == x.id && y.institutionId == null && y.groupId == null && !y.dateCeased
                                                )?.length
                                            }
                                        </span>
                                    </div>
                                </div>
                            ),
                            icon: null,
                            collapsible: true,
                            subTree: {
                                children: [
                                    ...createRoleNodes(
                                        personnel?.filter((z) => z.trialId == x.id && z.institutionId == null && z.groupId == null), 
                                        showCeased, 
                                        `trial-${x.id}`)
                                    ]
                            }
                        })) ?? [];
                return trialNodes;
            };

            const createInstitutionNodes = (institutions: Dtos.Institution[], personnel: Dtos.TrialPersonnel[], showCeased: boolean) => {
                let institutionNodes =
                    institutions
                        ?.sort((a, b) => a?.name?.localeCompare(b?.name))
                        ?.map((x) => ({
                            id: `institution-${x.id}`,
                            data: (
                                <div className="d-flex align-items-center">
                                    <div
                                        className="d-flex align-items-center justify-content-center"
                                        style={{ width: "25px" }}
                                    >
                                        <FontAwesomeIcon icon={FontAwesomeIcons.Duotone.INSTITUTION} />
                                    </div>
                                    <span
                                        className="pl-4 mb-0"
                                        style={{ width: "450px" }}
                                    >
                                        {x?.name}
                                    </span>
                                    <div
                                        className="d-flex align-items-center justify-content-center"
                                        style={{ width: "50px" }}
                                    >
                                        <span className="badge badge-pill badge-secondary ml-4 mb-0">
                                            {
                                                personnel?.filter((y) =>
                                                    showCeased
                                                        ? y.trialId == trialIdNumber && y.institutionId == x.id && y.groupId == null
                                                        : y.trialId == trialIdNumber && y.institutionId == x.id && y.groupId == null && !y.dateCeased
                                                )?.length
                                            }
                                        </span>
                                    </div>
                                </div>
                            ),
                            icon: null,
                            collapsible: true,
                            subTree: {
                                children: [
                                    ...createRoleNodes(
                                        personnel?.filter((z) => z.trialId == trialIdNumber && z.institutionId == x.id && z.groupId == null), 
                                        showCeased, 
                                        `institution-${x.id}`
                                    )
                                ]
                            }
                        })) ?? [];
                return institutionNodes;
            };

            const createGroupNodes = (groups: Dtos.Group[], personnel: Dtos.TrialPersonnel[], showCeased: boolean) => {
                let groupNodes =
                    groups
                        ?.sort((a, b) => a?.name?.localeCompare(b?.name))
                        ?.map((x) => ({
                            id: `group-${x.id}`,
                            data: (
                                <div className="d-flex align-items-center">
                                    <div
                                        className="d-flex align-items-center justify-content-center"
                                        style={{ width: "25px" }}
                                    >
                                        <FontAwesomeIcon icon={FontAwesomeIcons.Duotone.GROUP} />
                                    </div>
                                    <span
                                        className="pl-4 mb-0"
                                        style={{ width: "450px" }}
                                    >
                                        {x?.name}
                                    </span>
                                    <div
                                        className="d-flex align-items-center justify-content-center"
                                        style={{ width: "50px" }}
                                    >
                                        <span className="badge badge-pill badge-secondary ml-4 mb-0">
                                            {
                                                personnel?.filter((y) =>
                                                    showCeased
                                                        ? y.trialId == trialIdNumber && y.institutionId == null && y.groupId == x.id
                                                        : y.trialId == trialIdNumber && y.institutionId == null && y.groupId == x.id && !y.dateCeased
                                                )?.length
                                            }
                                        </span>
                                    </div>
                                </div>
                            ),
                            icon: null,
                            collapsible: true,
                            subTree: {
                                children: [
                                    ...createRoleNodes(
                                        personnel?.filter((z) => z.trialId == trialIdNumber && z.institutionId == null && z.groupId == x.id), 
                                        showCeased, 
                                        `group-${x.id}`)
                                    ]
                            }
                        })) ?? [];
                return groupNodes;
            };

            let uniqueTrials: Dtos.Trial[] = Array.from(
                new Map(
                    trialPersonnel
                        ?.filter((x) => x?.trial?.id && (showCeased ? true : !x.dateCeased))
                        ?.map((x) => [x.trial.id, x.trial])
                ).values()
            );

            let uniqueInstitutions: Dtos.Institution[] = Array.from(
                new Map(
                    trialPersonnel
                        ?.filter((x) => x?.institution?.id && (showCeased ? true : !x.dateCeased))
                        ?.map((x) => [x.institution.id, x.institution])
                ).values()
            );

            let uniqueGroups: Dtos.Group[] = Array.from(
                new Map(
                    trialPersonnel
                        ?.filter((x) => x?.group?.id && (showCeased ? true : !x.dateCeased))
                        ?.map((x) => [x.group.id, x.group])
                ).values()
            );

            let treeNodes = [
                ...createTrialNodes(uniqueTrials, trialPersonnel, showCeased),
                ...createInstitutionNodes(uniqueInstitutions, trialPersonnel, showCeased),
                ...createGroupNodes(uniqueGroups, trialPersonnel, showCeased)
            ];

            // Initialize tree toggle
            // const expanded = treeNodes.map((z) => z.id);

            // setToggleState(expanded);
            setTreeData({ children: treeNodes });
            setInitalizedTreeData(true);
            setCreatingTree(false);
        }
    }, [
        trialPersonnel,
        initalizedTreeData,
        treeData,
        setTreeData,
        setToggleState,
        setInitalizedTreeData,
        showCeased,
        setCreatingTree,
        trialIdNumber
    ]);

    const handleToggle = useCallback(
        (id: string, navigateFlag?: boolean) => {
            if (navigateFlag) {
                if (id.includes("personnel") && AuthenticationHelper.hasPermission(permissions, Dtos.Permission.TrialPersonnelRoleView)) {
                    const tpId = id.substring(id.lastIndexOf("-") + 1);
                    const personnel = trialPersonnel.find((x) => x.id.toString() == tpId);

                    if (id.includes("institution")) {
                        const trialinstitution = trialInstitutions.find((x) => x.trialId == personnel.trialId && x.institutionId == personnel.institutionId);
                        navigate(`/trials/${personnel?.trialId}/trialinstitution/${trialinstitution?.id}/personnel/${tpId}/details`);
                    }
                    else if (id.includes("group")) {
                        const trialgroup = trialGroups.find((x) => x.trialId == personnel.trialId && x.groupId == personnel.groupId);
                        navigate(`/trials/${personnel?.trialId}/groups/${personnel?.groupId}/trialgroup/${trialgroup?.id}/personnel/${tpId}/details`);
                    }
                    else {
                        navigate(`/trials/${trialIdNumber}/personnel/${tpId}/details`);
                    }
                }
                else {
                    setToggleState((prev) =>
                        prev.includes(id)
                            ? prev.filter((nodeId) => nodeId !== id)
                            : [...prev, id]
                    );
                }
            } else {
                setToggleState((prev) =>
                    prev.includes(id)
                        ? prev.filter((nodeId) => nodeId !== id)
                        : [...prev, id]
                );
            }
        },
        [setToggleState, navigate, trialIdNumber, permissions, trialPersonnel, trialInstitutions, trialGroups]
    );

    const onChange = (newValue) => {
        setInitalizedTreeData(false);
        setShowCeased(newValue);
    };

    // Refresh tree
    useEffect(() => {
        if (trialPersonnel) {
            setInitalizedTreeData(false);
        }
    }, [trialPersonnel, setInitalizedTreeData]);

    const renderContent = useMemo(() => {
        return (
            <div>
                <div className="row">
                    <div className="col-md-12 col-lg-auto mb-2">
                        <h1>Trial Personnel</h1>
                    </div>
                    <div
                        className="col-md-12 col-lg d-flex justify-content-md-start justify-content-lg-end align-items-stretch mb-2 mb-lg-0"
                        style={{ height: "38px" }}
                    >
                        {trial &&
                            trial.id > 0 &&
                            AuthenticationHelper.hasPermission(
                                permissions,
                                Dtos.Permission.TrialPersonnelRoleCreate,
                                null,
                                trial.id
                            ) && (
                                <>
                                    <ToggleButton
                                        id="toggle-button-ceased"
                                        checked={showCeased}
                                        onChange={onChange}
                                        labelA="Hide ceased personnel"
                                        labelB="Show ceased personnel"
                                        style={{ minWidth: "200px" }}
                                    />
                                    <a
                                        className="btn btn-info mr-2"
                                        target="_blank"
                                        href={encodeURI(
                                            `/api/personnelrole/export/${trial.id.toString()}/0/0/0`
                                        )}
                                    >
                                        <FontAwesomeIcon icon={FontAwesomeIcons.Solid.CLOUD_DOWNLOAD} fixedWidth />
                                    </a>
                                    <Link
                                        className={"btn btn-primary"}
                                        url={`/trials/${trial.id}/personnel/create`}
                                    >
                                        Create
                                    </Link>
                                </>
                            )}
                    </div>
                </div>
                {!showTrialPersonnels ? (
                    <InlineLoading absoluteCenter={true} size={50} />
                ) : null}
                {showTrialPersonnels ? (
                    <>
                        {treeData && treeData?.children?.length > 0 ? (
                            <Tree
                                treeData={treeData}
                                onToggle={handleToggle}
                                toggleState={toggleState}
                            />
                        ) : (
                            <div className="card card-shadow p-3">
                                <div className="alert alert-info d-flex mb-0">
                                    <div className="mr-2">
                                        <FontAwesomeIcon icon={FontAwesomeIcons.Solid.INFO_SQUARE} fixedWidth />
                                    </div>
                                    <span>
                                        There are currently no trial personnel
                                    </span>
                                </div>
                            </div>
                        )}
                    </>
                ) : null}
            </div>
        );
    }, [
        trial,
        showTrialPersonnels,
        showCeased,
        treeData,
        handleToggle,
        toggleState,
    ]);

    return (
        <TrialLayout
            loading={
                loadingTrial || 
                loadingTrialPersonnel || 
                loadingTrialInstitutions ||
                loadingTrialGroups
            }
            permission={Dtos.Permission.TrialPersonnelRoleView}
        >
            {renderContent}
        </TrialLayout>
    );
};

const mapStateToProps = (state: ITmdState): Partial<TrialPersonnelPageProps> => {
    let trial: Dtos.Trial | undefined = !(state.trials.formData instanceof Array)
        ? state.trials.formData
        : undefined;
    let trialPersonnel: Dtos.TrialPersonnel[] | undefined = (state.trialPersonnel.formData instanceof Array)
        ? state.trialPersonnel.formData
        : undefined;
    let trialInstitutions: Dtos.TrialInstitution[] | undefined = (state.trialInstitution.formData instanceof Array)
            ? state.trialInstitution.formData
            : undefined;
    let trialGroups: Dtos.TrialGroup[] | undefined = (state.trialGroup.formData instanceof Array)
            ? state.trialGroup.formData
            : undefined;
    let permissions = state.user.userSummary ? state.user.userSummary.permissions : [];

    return {
        trial: trial!,
        loadingTrial: state.trials.loadState && state.trials.loadState.status == RequestState.Pending,
        loadTrialSuccess: state.trials.loadState && state.trials.loadState.status == RequestState.Success,
        loadTrialFailure: state.trials.loadState && state.trials.loadState.status == RequestState.Failure,
        loadTrialErrorCode: state.trials.loadState.errorCode,
        trialPersonnel: trialPersonnel!,
        loadingTrialPersonnel: state.trialPersonnel.loadState && state.trialPersonnel.loadState.status == RequestState.Pending,
        loadTrialPersonnelSuccess: state.trialPersonnel.loadState && state.trialPersonnel.loadState.status == RequestState.Success,
        loadTrialPersonnelFailure: state.trialPersonnel.loadState && state.trialPersonnel.loadState.status == RequestState.Failure,
        loadTrialPersonnelErrorCode: state.trialPersonnel.loadState.errorCode,
        trialInstitutions: trialInstitutions!,
        loadingTrialInstitutions: state.trialInstitution.loadState && state.trialInstitution.loadState.status == RequestState.Pending,
        loadTrialInstitutionsSuccess: state.trialInstitution.loadState && state.trialInstitution.loadState.status == RequestState.Success,
        loadTrialInstitutionsFailure: state.trialInstitution.loadState && state.trialInstitution.loadState.status == RequestState.Failure,
        loadTrialInstitutionsErrorCode: state.trialInstitution.loadState.errorCode,
        trialGroups: trialGroups!,
        loadingTrialGroups: state.trialGroup.loadState && state.trialGroup.loadState.status == RequestState.Pending,
        loadTrialGroupsSuccess: state.trialGroup.loadState && state.trialGroup.loadState.status == RequestState.Success,
        loadTrialGroupsFailure: state.trialGroup.loadState && state.trialGroup.loadState.status == RequestState.Failure,
        loadTrialGroupsErrorCode: state.trialGroup.loadState.errorCode,
        permissions: permissions,
    };
};

const mapDispatchToProps = (dispatch): Partial<TrialPersonnelPageProps> => {
    return {
        loadTrial: bindActionCreators(TrialActions.LoadTrialById, dispatch),
        clearTrial: bindActionCreators(TrialActions.Clear, dispatch),
        loadTrialPersonnel: bindActionCreators(TrialPersonnelActions.LoadTrialPersonnelsByTrialId, dispatch),
        clearTrialPersonnel: bindActionCreators(TrialPersonnelActions.Clear, dispatch),
        loadTrialInstitutions: bindActionCreators(TrialInstitutionActions.LoadTrialInstitutionsByTrialId, dispatch),
        clearTrialInstitutions: bindActionCreators(TrialInstitutionActions.Clear, dispatch),
        loadTrialGroups: bindActionCreators(TrialGroupActions.LoadTrialGroupsByTrialId, dispatch),
        clearTrialGroups: bindActionCreators(TrialGroupActions.Clear, dispatch),
        setBreadcrumbs: bindActionCreators(NavigationActions.SetBreadcrumbs, dispatch),
        setDrawerItems: bindActionCreators(NavigationActions.SetDrawerItems, dispatch)
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(TrialPersonnelsPage);
